import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import CreditMeter from '@/components/CreditMeter';
import UserMenu from '@/components/UserMenu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Lock, CreditCard, ArrowUpRight, AlertTriangle, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = { free: 'var(--orbis-t4)', pro: 'var(--orbis-blue)', enterprise: 'var(--orbis-amber)' };

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { tier, monthlyCredits, balance } = useCredits();

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Deletion flow
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const isPendingDeletion = user?.status === 'pending_deletion';
  const deletionDate = user?.deletion_scheduled_at
    ? new Date(user.deletion_scheduled_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  const handleRequestDeletion = async () => {
    if (deleteConfirmEmail.toLowerCase() !== user?.email?.toLowerCase()) {
      toast.error('Email does not match your account email');
      return;
    }
    setDeleteLoading(true);
    try {
      await axios.post(`${API_URL}/auth/delete-account`);
      setShowDeleteModal(false);
      toast.success('Account scheduled for deletion. You have 30 days to cancel.');
      logout();
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule deletion');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${API_URL}/auth/cancel-deletion`);
      toast.success('Account deletion cancelled — your account is active again');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel deletion');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    const updates = {};
    if (name !== user?.name) updates.name = name;
    if (email !== user?.email) updates.email = email;
    if (Object.keys(updates).length === 0) { toast.info('No changes to save'); return; }

    setProfileSaving(true);
    try {
      await axios.patch(`${API_URL}/auth/me`, updates);
      toast.success('Profile updated');
      // Refresh the page to reflect changes in AuthContext
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) { toast.error('Enter your current password'); return; }
    if (!newPassword) { toast.error('Enter a new password'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setPasswordSaving(true);
    try {
      await axios.patch(`${API_URL}/auth/me`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const tierColor = TIER_COLORS[tier] || 'var(--orbis-t4)';
  const tierLabel = tier?.toUpperCase() || 'FREE';

  const inputStyle = {
    background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)',
    color: 'var(--orbis-t1)', fontSize: 14, fontFamily: 'var(--orbis-font)',
    borderRadius: 'var(--orbis-r-md)',
  };
  const labelStyle = { fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', fontWeight: 500 };

  return (
    <div className="min-h-screen" style={{ background: 'var(--surface-0)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 flex items-center justify-between" style={{ height: 64, padding: '0 20px', background: 'var(--surface-0)', borderBottom: '0.5px solid var(--orbis-s2)' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer" style={{ background: 'none', border: 'none', padding: 0 }}>
            <OrbisMarkSVG size={22} />
            <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>Orbis</span>
          </button>
          <span style={{ fontSize: 14, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}>/</span>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>Account Settings</span>
        </div>
        <div className="flex items-center gap-3">
          <CreditMeter />
          <UserMenu />
        </div>
      </header>

      <main style={{ maxWidth: 640, margin: '0 auto', padding: '40px 24px' }}>
        {/* Profile Section */}
        <section style={{
          background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)',
          borderRadius: 14, padding: 28, marginBottom: 24,
        }}>
          <div className="flex items-center gap-2.5 mb-5">
            <User className="w-4 h-4" style={{ color: 'var(--orbis-t3)' }} />
            <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}>Profile</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label style={labelStyle}>Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="Your name" />
            </div>
            <div>
              <Label style={labelStyle}>Email</Label>
              <Input value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="your@email.com" type="email" />
            </div>
            <button
              onClick={handleSaveProfile}
              disabled={profileSaving}
              className="cursor-pointer"
              style={{
                background: 'var(--orbis-blue)', color: '#09090B',
                fontSize: 13, fontWeight: 600, fontFamily: 'var(--orbis-font)',
                padding: '8px 20px', borderRadius: 100, border: 'none',
                opacity: profileSaving ? 0.6 : 1,
              }}
            >
              {profileSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </section>

        {/* Password Section */}
        <section style={{
          background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)',
          borderRadius: 14, padding: 28, marginBottom: 24,
        }}>
          <div className="flex items-center gap-2.5 mb-5">
            <Lock className="w-4 h-4" style={{ color: 'var(--orbis-t3)' }} />
            <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}>Change Password</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label style={labelStyle}>Current Password</Label>
              <Input value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={inputStyle} type="password" placeholder="Enter current password" />
            </div>
            <div>
              <Label style={labelStyle}>New Password</Label>
              <Input value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} type="password" placeholder="At least 6 characters" />
            </div>
            <div>
              <Label style={labelStyle}>Confirm New Password</Label>
              <Input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={inputStyle} type="password" placeholder="Repeat new password" />
            </div>
            <button
              onClick={handleChangePassword}
              disabled={passwordSaving}
              className="cursor-pointer"
              style={{
                background: 'transparent', color: 'var(--orbis-t2)',
                fontSize: 13, fontWeight: 500, fontFamily: 'var(--orbis-font)',
                padding: '8px 20px', borderRadius: 100,
                border: '0.5px solid var(--orbis-s3)',
                opacity: passwordSaving ? 0.6 : 1,
              }}
            >
              {passwordSaving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </section>

        {/* Subscription & Tier */}
        <section style={{
          background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)',
          borderRadius: 14, padding: 28, marginBottom: 24,
        }}>
          <div className="flex items-center gap-2.5 mb-5">
            <CreditCard className="w-4 h-4" style={{ color: 'var(--orbis-t3)' }} />
            <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}>Subscription</h2>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span style={{ fontSize: 20, fontFamily: 'var(--orbis-mono)', fontWeight: 600, color: 'var(--orbis-t1)' }}>
                  {tierLabel}
                </span>
                <span style={{
                  fontSize: 9, fontFamily: 'var(--orbis-mono)', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '1px',
                  padding: '3px 10px', borderRadius: 100,
                  border: `0.5px solid ${tierColor}40`, color: tierColor, background: `${tierColor}10`,
                }}>
                  Active
                </span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>
                {monthlyCredits} credits/month &middot; {balance} remaining
              </p>
            </div>

            <button
              onClick={() => navigate('/billing')}
              className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
              style={{
                background: 'transparent', color: 'var(--orbis-blue)',
                fontSize: 13, fontWeight: 500, fontFamily: 'var(--orbis-font)',
                padding: '8px 16px', borderRadius: 100,
                border: `0.5px solid var(--orbis-blue)40`,
              }}
            >
              Manage Billing <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </section>

        {/* Danger Zone */}
        <section style={{
          background: 'var(--orbis-s1)',
          border: `0.5px solid ${isPendingDeletion ? 'rgba(248,113,113,0.4)' : 'rgba(248,113,113,0.15)'}`,
          borderRadius: 14, padding: 28,
        }}>
          <div className="flex items-center gap-2.5 mb-4">
            <AlertTriangle className="w-4 h-4" style={{ color: '#F87171' }} />
            <h2 style={{ fontSize: 16, fontWeight: 500, color: '#F87171', fontFamily: 'var(--orbis-font)' }}>Danger Zone</h2>
          </div>

          {isPendingDeletion ? (
            /* ── Pending deletion banner ── */
            <div style={{ background: 'rgba(248,113,113,0.07)', border: '0.5px solid rgba(248,113,113,0.25)', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: '#F87171', marginBottom: 6, fontFamily: 'var(--orbis-font)' }}>
                Your account is scheduled for deletion on {deletionDate}.
              </p>
              <p style={{ fontSize: 12, color: 'var(--orbis-t3)', marginBottom: 14, fontFamily: 'var(--orbis-font)', lineHeight: 1.6 }}>
                All your projects, domains, and data will be permanently removed on that date.
                Log back in before then to cancel.
              </p>
              <button
                onClick={handleCancelDeletion}
                disabled={cancelLoading}
                className="cursor-pointer"
                style={{
                  background: 'rgba(248,113,113,0.12)', color: '#F87171',
                  fontSize: 13, fontWeight: 500, fontFamily: 'var(--orbis-font)',
                  padding: '8px 20px', borderRadius: 100,
                  border: '0.5px solid rgba(248,113,113,0.35)',
                  opacity: cancelLoading ? 0.6 : 1,
                }}
              >
                {cancelLoading ? 'Cancelling...' : 'Cancel Deletion'}
              </button>
            </div>
          ) : (
            /* ── Active account ── */
            <>
              <p style={{ fontSize: 13, color: 'var(--orbis-t3)', marginBottom: 16, fontFamily: 'var(--orbis-font)', lineHeight: 1.6 }}>
                Permanently delete your account and all associated data — projects, domains, images, and billing history.
                You'll have <strong style={{ color: 'var(--orbis-t2)' }}>30 days</strong> to cancel before anything is removed.
              </p>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="cursor-pointer"
                style={{
                  background: 'transparent', color: '#F87171',
                  fontSize: 13, fontWeight: 500, fontFamily: 'var(--orbis-font)',
                  padding: '8px 20px', borderRadius: 100,
                  border: '0.5px solid rgba(248, 113, 113, 0.3)',
                }}
              >
                Delete Account
              </button>
            </>
          )}
        </section>
      </main>

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}
        >
          <div style={{
            background: 'var(--orbis-s1)', border: '0.5px solid rgba(248,113,113,0.25)',
            borderRadius: 16, padding: 32, width: '100%', maxWidth: 420, margin: '0 16px',
          }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                <AlertTriangle className="w-4 h-4" style={{ color: '#F87171' }} />
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F87171', fontFamily: 'var(--orbis-font)' }}>Delete Account</h3>
              </div>
              <button onClick={() => setShowDeleteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--orbis-t3)' }}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div style={{ background: 'rgba(248,113,113,0.06)', border: '0.5px solid rgba(248,113,113,0.2)', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
              <p style={{ fontSize: 12, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)', lineHeight: 1.7, margin: 0 }}>
                This will schedule deletion of your account and all data in <strong>30 days</strong>.
                You can cancel any time before that by logging back in.
                After 30 days, all projects, domains, images, and billing history are <strong>permanently removed</strong>.
              </p>
            </div>

            <div className="mb-5">
              <Label style={{ fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', fontWeight: 500, display: 'block', marginBottom: 6 }}>
                Type your email to confirm: <span style={{ color: 'var(--orbis-t2)' }}>{user?.email}</span>
              </Label>
              <Input
                type="email"
                placeholder={user?.email}
                value={deleteConfirmEmail}
                onChange={e => setDeleteConfirmEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRequestDeletion()}
                style={{
                  background: 'var(--orbis-bg)', border: '0.5px solid rgba(248,113,113,0.3)',
                  color: 'var(--orbis-t1)', fontSize: 14, fontFamily: 'var(--orbis-font)',
                  borderRadius: 'var(--orbis-r-md)',
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="cursor-pointer flex-1"
                style={{
                  background: 'transparent', color: 'var(--orbis-t2)',
                  fontSize: 13, fontWeight: 500, fontFamily: 'var(--orbis-font)',
                  padding: '10px', borderRadius: 100,
                  border: '0.5px solid var(--orbis-s3)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRequestDeletion}
                disabled={deleteLoading || deleteConfirmEmail.toLowerCase() !== user?.email?.toLowerCase()}
                className="cursor-pointer flex-1"
                style={{
                  background: deleteConfirmEmail.toLowerCase() === user?.email?.toLowerCase() ? 'rgba(248,113,113,0.15)' : 'transparent',
                  color: '#F87171',
                  fontSize: 13, fontWeight: 500, fontFamily: 'var(--orbis-font)',
                  padding: '10px', borderRadius: 100,
                  border: '0.5px solid rgba(248,113,113,0.35)',
                  opacity: deleteLoading ? 0.6 : 1,
                  transition: 'background 0.2s',
                }}
              >
                {deleteLoading ? 'Scheduling...' : 'Schedule Deletion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
